import { PosState } from "../../../stores/PosSlice";
import {
  getProductIdOrTicketIdFromProduct,
  isPaidRedeemItem,
  isSameProduct,
  updateStateForPrePurchasedItem,
} from "./Product";
import { PointOfSaleCategory } from "../enums/PointOfSaleCategory";
import { getBundleItemQuantities } from "./Bundle";
import { removeRedeemedTicketsBasedOnUpgrade } from "./TicketUpgrade";
import {
  CartItem,
  PrePurchasedItem,
  PurchaseItemViewModel,
  Product,
  TicketUpgradeDataModel,
} from "../models";

// --- Ticket Allocation Helper Functions ---

/** Calculates the number of tickets remaining for purchase & redemption */
export const remainingTickets = (
  itemsInCart: CartItem[],
  itemsForRedemption: PrePurchasedItem[],
  numberOfCheckedInParticipants: number
) => {
  const ticketsCountInCart =
    itemsInCart?.reduce(
      (acc: number, cartItem: CartItem) =>
        isTicket(cartItem) && !cartItem.isUpgradedCartTicket
          ? acc + cartItem.quantity
          : acc,
      0
    ) || 0;

  const bundleTicketsCountInCart = itemsInCart
    ?.filter((item: CartItem) => item.isBundle)
    .reduce((sum, bundle) => {
      const ticketsPerBundle =
        getBundleItemQuantities(bundle).bundleTicketQty || 0;
      return sum + ticketsPerBundle * (bundle.quantity || 1);
    }, 0);

  const ticketsCountForRedemption =
    itemsForRedemption.reduce(
      (acc: number, item: PrePurchasedItem) =>
        isTicket(item) ? acc + item.quantity : acc,
      0
    ) || 0;

  return (
    numberOfCheckedInParticipants -
    (ticketsCountInCart + bundleTicketsCountInCart + ticketsCountForRedemption)
  );
};

/** Returns true if item is of type Ticket */
export const isTicket = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => item.type === PointOfSaleCategory.Ticket;

/** Returns true if item is a Parent Ticket (productLevelId === 6) */
export const isParentTicket = (item: CartItem | PrePurchasedItem) =>
  item.type === PointOfSaleCategory.Ticket && item.productLevelId === 6;

/** Decreases the quantity of a ticket in itemsInCart */
export const removePurchasedPasses = (state: PosState, cartItem?: CartItem) => {
  if (!cartItem) return;
  state.itemsInCart = state.itemsInCart
    ?.map((item: CartItem) =>
      isTicket(item) && isSameProduct(cartItem, item)
        ? { ...item, quantity: item.quantity - 1 }
        : item
    )
    .filter((item) => item.quantity > 0);
};

/** Checks if two number arrays are equal */
export const areNumberArraysEqual = (
  people1?: number[],
  people2?: number[]
): boolean =>
  !!people1 &&
  !!people2 &&
  people1.length === people2.length &&
  people1.every((person, idx) => person === people2[idx]);

/** Returns true if any parent-ticket is in cart */
export const hasParentPassInItemInCart = (itemsInCart?: CartItem[]) =>
  !!itemsInCart?.length && itemsInCart.some(isParentTicket);

/** Returns true if any non-parent-ticket is in cart */
export const hasNonParentPassInItemsInCart = (itemsInCart?: CartItem[]) =>
  !!itemsInCart?.length &&
  itemsInCart.some((item: CartItem) => isTicket(item) && !isParentTicket(item));

/** Returns true if any non-parent-ticket is for redemption */
export const hasNonParentPassInItemsForRedemption = (
  itemsForRedemption?: PrePurchasedItem[]
) =>
  !!itemsForRedemption?.length &&
  itemsForRedemption.some(
    (item: PrePurchasedItem) => isTicket(item) && !isParentTicket(item)
  );

/** Returns true if any non-parent-ticket is admitted */
export const hasNonParentPassInAdmittedTickets = (
  admittedTickets?: Product[]
) =>
  !!admittedTickets?.length &&
  admittedTickets.some(
    (item: PrePurchasedItem) => isTicket(item) && !isParentTicket(item)
  );

// Calculate the price difference for an upgrade
export const getPriceDifference = (
  ticketId: number,
  availableTicketId: number,
  products: Product[]
) => {
  const ticket = products.find(
    (product: Product) =>
      isTicket(product) &&
      getProductIdOrTicketIdFromProduct(product) === ticketId
  );
  const availableTicket = products.find(
    (product: Product) =>
      isTicket(product) &&
      getProductIdOrTicketIdFromProduct(product) === availableTicketId
  );
  if (ticketId !== availableTicketId) {
    return ((availableTicket?.price ?? 0) - (ticket?.price ?? 0)).toFixed(2);
  }
  return undefined;
};

// Helper function to handle redeeming item reset
export const handleRedeemingItemReset = (
  state: PosState,
  item: PrePurchasedItem
): void => {
  // Reset quantity for items with paid product options
  if (isPaidRedeemItem(item)) {
    updateStateForPrePurchasedItem(state, {
      ...item,
      quantity: 0,
    });
  }

  // Handle upgraded redeemed tickets
  if (isTicket(item)) {
    // Find and remove all relevant ticket upgrade data items for this specific ticket
    const relevantUpgradeDataItems = state.ticketUpgradeData.filter(
      (data: TicketUpgradeDataModel) =>
        data.ticketId === item.ticketId && !data.isAdmitted
    );

    if (relevantUpgradeDataItems.length > 0 && relevantUpgradeDataItems[0]) {
      removeRedeemedTicketsBasedOnUpgrade(
        state,
        relevantUpgradeDataItems[0].ticketId,
        relevantUpgradeDataItems.length
      );
    }
  }
};
